import { action, observable, makeObservable } from 'mobx';
import { BaseViewStore, HyperlinkCellData } from 'core/stores';
import { FulfillmentOrderProcessingStore, FulfillmentOrderStore } from 'modules/order/stores';
import { PackingSlipStore } from 'modules/packing-slip/stores';
import { LookupStore } from 'modules/lookup/stores';
import { ProductStore } from 'modules/product/stores';
import { LabelingStepForm, DimensionsStepForm, FinalStepForm } from 'modules/order/forms/labeling-mode';
import { PalletCreateForm } from 'modules/order/forms';
import {
	isSeller,
	isSuperAdminOrganization,
	isWarehouse,
	createFileUrl,
	notificationError,
	translate,
} from 'core/utils';
import { lookupRouteName, sourceOfChange, fulfillmentOrderStatus, shipmentInfoStatusEnum } from 'common/constants';
import { BaasicDropdownViewStore } from 'common/stores';
import { ShipmentInfoStore } from 'modules/shipment-info/stores';
import { SoundService } from 'modules/sound/services';
import { PalletStore } from 'modules/order/stores';
import moment from 'moment';
import * as printJS from 'print-js';
import _ from 'lodash';

class labelingModeViewStore extends BaseViewStore {
	@observable scannedUpc = '';
	@observable barcode = null;
	@observable isUpcScanned = false;
	@observable fulfillmentOrders = [];
	@observable activeStep = {};
	@observable scanUpcMessage = null;
	@observable scanLabelMessage = null;
	@observable closeBoxMessage = null;
	@observable createLabelMessage = null;
	@observable dimensionsStepErrorMessage = null;
	@observable infiniteSearchPhrase = null;
	@observable showCreateTemplate = false;
	@observable newFulfillmentOrderCreated = false;
	@observable selectedFulfillmentOrder = null;
	@observable initialFulfillmentOrder = null;
	@observable closeBoxDisabled = false;
	@observable lastAction = null;
	@observable timeoutAction = null;
	@observable actionsDone = [];
	@observable undoDisabled = false;
	@observable isFOSplitted = false;
	@observable createPalletMode = false;
	@observable createPalletNotification = null;
	@observable steps = {};
	data = [];
	selectedItem;
	tableData = null;
	originalBarcode = null;
	barcodeRef = null;
	selectReference = null;
	product = null;
	activeShipmentInfo = null;
	nonScannedItems = [];
	packagedFulfillmentOrders = [];
	boxedFulfillmentOrders = [];
	splittedFulfillmentOrderIds = [];
	label;

	getRemoveFromPalletConfirmModalProps = item => {
		return {
			onConfirm: async () => await this.removeFromPallet(this.rootStore.viewStore.selectedConfirmModal.data),
			message: ['EXITSCANDETAILVIEW.MODAL.REMOVEFROMPALLET', { name: item.fulfillmentOrder.fulfillmentOrderId }],
			data: item,
		};
	};

	palletCreateForm = new PalletCreateForm({
		onSuccess: async () => {
			await this.createPallet();
		},
	});

	constructor(rootStore, { modalParams }) {
		super(rootStore);
		makeObservable(this);
		this.modalParams = modalParams;
		this.useRegularPalletizingMode = rootStore.orderListViewStore.useRegularPalletizingMode;
		this.fulfillmentOrderProcessingStore = new FulfillmentOrderProcessingStore(rootStore);
		this.fulfillmentOrderStore = new FulfillmentOrderStore(rootStore);
		this.packingSlipStore = new PackingSlipStore(rootStore);
		this.shipmentInfoStore = new ShipmentInfoStore(rootStore);
		this.productStore = new ProductStore(rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.soundService = new SoundService();
		this.isSeller = isSeller(rootStore);
		this.isWarehouse = isWarehouse(rootStore);
		this.isSuperAdminOrganization = isSuperAdminOrganization(rootStore);
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		this.suspend();
		if (!this.useRegularPalletizingMode) {
			this.palletStore = new PalletStore(this.rootStore);
			this.palletDropdownStore = new BaasicDropdownViewStore();
			await this.palletDropdownStore.initialize({
				store: new PalletStore(this.rootStore),
				itemTemplate: {
					value: 'id',
					label: 'barcode',
				},
				isRequired: true,
				filterProperties: {
					embed: [
						'shipmentInfos',
						'shipmentInfos.fulfillmentOrder',
						'shipmentInfos.fulfillmentOrder.order',
						'shipmentInfos.fulfillmentOrder.order.channel',
						'shipmentInfos.fulfillmentOrder.order.channel.channelType',
						'shipmentInfos.fulfillmentOrder.fulfillmentOrderStatus',
						'shipmentInfos.fulfillmentOrder.fulfillmentOrderItems',
						'shipmentInfos.fulfillmentOrder.fulfillmentOrderItems.product',
						'carrierTypeServiceType',
					],
					isOpened: true,
					isSeller: isSeller(this.rootStore),
				},
				onChangeCallback: this.populateTableCallback,
			});
			this.carrierTypeServiceTypeDropdownStore = new BaasicDropdownViewStore();
			await this.carrierTypeServiceTypeDropdownStore.initialize({
				field: this.palletCreateForm.$('carrierTypeServiceTypeId'),
				lookupRoute: lookupRouteName.carrierTypeServiceType,
				store: new LookupStore(this.rootStore),
				onChangeCallback: option => {
					if (option) this.barcode = `${option.extra.code}-${this.originalBarcode}`;
				},
			});
			this.defineFOTableData();
		}
		this.initializeSteps();
		this.boxDropdownStore = new BaasicDropdownViewStore();
		await this.boxDropdownStore.initialize({
			field: this.steps.dimensionsStep.form.$('boxId'),
			store: new LookupStore(this.rootStore),
			lookupRoute: lookupRouteName.box,
			onChangeCallback: value => this.onBoxChange(value),
			fetchInitialValues: false,
			headerOption: { value: null, label: 'Add new template' },
		});
		this.resume();
	};

	@action.bound
	initializeSteps = () => {
		this.steps = {
			selectingFoStep: {
				title: 'FULFILLMENTORDER.LABELINGMODE.STEPS.SELECTINGFO',
				number: 1,
				isActive: true,
				isCompleted: false,
			},
			labelingStep: {
				title: 'FULFILLMENTORDER.LABELINGMODE.STEPS.LABELING',
				number: 2,
				isActive: false,
				isCompleted: false,
				form: new LabelingStepForm(),
			},
			dimensionsStep: {
				title: 'FULFILLMENTORDER.LABELINGMODE.STEPS.DIMENSIONS',
				number: 3,
				isActive: false,
				isCompleted: false,
				form: new DimensionsStepForm({
					onSuccess: () => {
						this.onCreateTemplateSubmit();
					},
				}),
			},
			finalStep: {
				title: this.useRegularPalletizingMode
					? 'FULFILLMENTORDER.LABELINGMODE.STEPS.PACKAGING'
					: 'FULFILLMENTORDER.LABELINGMODE.STEPS.PALLETIZING',
				number: 4,
				isActive: false,
				isCompleted: false,
				form: new FinalStepForm(),
			},
		};
	};

	@action.bound
	deletePallet = async () => {
		this.suspend();
		try {
			const option = this.rootStore.viewStore.selectedConfirmModal.data;
			await this.palletStore.delete({ id: option.value });
			this.rootStore.notificationStore.success(['LOOKUPDELETE.PALLET', { name: option.label }]);
			await this.palletDropdownStore.reload();
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	displayDeleteAction = data => {
		if (
			data.extra != null &&
			data.extra.shipmentInfos != null &&
			data.extra.shipmentInfos.length == 0 &&
			(isSuperAdminOrganization(this.rootStore) || isWarehouse(this.rootStore))
		) {
			return true;
		} else {
			return false;
		}
	};

	@action.bound
	populateTableCallback = selectedValue => {
		this.data = [];
		_.each(selectedValue.extra.shipmentInfos, shipmentInfo => {
			if (shipmentInfo != null && shipmentInfo.fulfillmentOrder != null) {
				let product = {};
				if (shipmentInfo.fulfillmentOrder.fulfillmentOrderItems.length != 1) {
					product.name = 'Multiple';
					product.sku = 'Multiple';
				} else {
					product.name = shipmentInfo.fulfillmentOrder.fulfillmentOrderItems[0].product.name;
					product.sku = shipmentInfo.fulfillmentOrder.fulfillmentOrderItems[0].product.mainSku;
				}
				shipmentInfo.fulfillmentOrder.product = product;
				this.data.push(shipmentInfo);
			}
		});
	};

	@action.bound
	removeFromPallet = async item => {
		this.suspend();
		try {
			await this.shipmentInfoStore.removeFromPallet({
				id: item.id,
				sourceOfChange: sourceOfChange.palletizingModeRemoveFromPallet,
			});
			this.rootStore.notificationStore.success([
				'EXITSCANDETAILVIEW.TABLE.ACTION.NOTIFICATION.REMOVEFROMPALLET',
				{ name: item.fulfillmentOrder.fulfillmentOrderId },
			]);
			await this.palletDropdownStore.reload();
			this.palletDropdownStore.setValue(
				_.find(this.palletDropdownStore.options, palletOption => {
					return palletOption.value == this.palletDropdownStore.value.value;
				})
			);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	defineFOTableData = () => {
		this.tableData = {
			columns: [
				{
					key: 'fulfillmentOrder.fulfillmentOrderId',
					title: 'FULFILLMENTORDER.PALLETIZINGMODE.TABLEDATA.FULFILLMENTORDERID',
					dataCell: HyperlinkCellData,
					onClick: dataItem =>
						this.rootStore.routerStore.goTo('master.app.main.order.list.detail', {
							id: dataItem.id,
						}),
					selected: true,
					width: 150,
					filter: null,
				},
				{
					key: 'fulfillmentOrder.fulfillmentOrderStatus.name',
					title: 'FULFILLMENTORDER.PALLETIZINGMODE.TABLEDATA.STATUS',
					selected: true,
					width: 150,
					filter: null,
				},
				{
					key: 'fulfillmentOrder.product.sku',
					title: 'FULFILLMENTORDER.PALLETIZINGMODE.TABLEDATA.PRODUCTSKU',
					selected: true,
					width: 150,
					filter: null,
				},
			],
			actions: {
				onRemoveFromPallet: async item => this.openConfirmationModal(this.getRemoveFromPalletConfirmModalProps(item)),
			},
			sortObject: [
				{
					field: 'fulfillmentOrderId',
					dir: 'asc',
				},
			],
		};
		this.permissions = {
			onRemoveFromPallet: () => this.rootStore.authStore.hasPermission('fulfillmentOrderSection.full'),
		};
	};

	@action.bound
	returnToPreviousStep = async () => {
		if (this.activeStep.number > 1) {
			await this.changeStep(this.activeStep.number - 1);
		}
	};

	@action.bound
	togglePallet = () => {
		this.data = [];
		this.carrierTypeServiceTypeDropdownStore.clear();
		this.palletCreateForm.clear();
		this.palletFindNotification = null;
		this.createPalletNotification = null;
		this.createPalletMode = !this.createPalletMode;
		if (this.createPalletMode) {
			this.palletCreateForm.$('name').value = moment().toString();
			this.generateBarcode();
		}
	};

	@action.bound
	generateBarcode = () => {
		var result = '';
		const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
		const charactersLength = characters.length;
		for (var i = 0; i < 10; i++) {
			result += characters.charAt(Math.floor(Math.random() * charactersLength));
		}
		this.barcode = result;
		this.originalBarcode = result;
	};

	@action.bound
	createPallet = async () => {
		try {
			const { warehouseId, ...otherValues } = this.palletCreateForm.values();
			if (this.barcodeRef != null) {
				var label = this.barcodeRef.refs.renderElement.currentSrc.split(',')[1];
			}
			const response = await this.palletStore.create(
				{
					barcode: this.barcode,
					open: true,
					label,
					...otherValues,
				},
				sourceOfChange.palletizingModeCreatePallet
			);
			if (response.hasOwnProperty('statusCode')) {
				this.rootStore.notificationStore.success('FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PRINTJOBCREATED');
			} else {
				this.rootStore.notificationStore.warning('FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PRINTNODEINFONOTFOUND');
			}
			if (response.hasOwnProperty('pallets')) {
				this.pallet = response.pallets[0];
			} else {
				this.pallet = response[0];
			}
			this.palletCreateForm.clear();
			this.createPalletMode = false;
			this.createPalletNotification = 'FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PALLET.SUCCESS';
			this.palletDropdownStore.setValue(this.pallet);
			await this.palletDropdownStore.reload();
			this.carrierTypeServiceTypeDropdownStore.clear();
		} catch (err) {
			if (err.statusCode == 409) {
				this.createPalletNotification = 'FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PALLET.CONFLICT';
			} else {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		}
	};

	@action.bound
	onPrintLabel(label) {
		const fileUrl = createFileUrl(label);
		printJS(fileUrl);
	}

	@action.bound
	changeStep = async stepNumber => {
		_.forEach(this.steps, step => {
			if (step.number === stepNumber) {
				step.isActive = true;
				step.isCompleted = false;
				this.activeStep = step;
			} else {
				step.isActive = false;
				if (step.number < stepNumber) {
					step.isCompleted = true;
				}
			}
		});
		this.resetMessages();
		// UPC input is visible on steps 1 and 2 and it has to be focused for the user.
		if ((this.activeStep.number === 1 || this.activeStep.number === 2) && this.upcInput) {
			// Initially scanned UPC should be always selected on step 1.
			if (this.activeStep.number === 1) {
				this.steps.labelingStep.form.$('upc').value = this.scannedUpc;
			}
			this.upcInput.focus();
		}
		if (this.activeStep.number === 3) {
			this.boxDropdownStore.reload();
		}
	};

	@action.bound
	onItemScan = scannedUpc => {
		let notFoundItems = 0;
		const totalItemCount = this.selectedFulfillmentOrder.fulfillmentOrderItems.length;
		_.forEach(this.selectedFulfillmentOrder.fulfillmentOrderItems, item => {
			if (item.product.productUpcs.some(upc => upc.upc === scannedUpc)) {
				if (item.scannedQuantity < item.quantity) {
					item.scannedQuantity++;
					if (item.scannedQuantity === item.quantity) {
						// Item is marked as scanned only if all quantity of the item is scanned.
						item.isScanned = !item.isScanned;
					}
					this.soundService.success();
				} else {
					this.soundService.error();
				}
				return false; // Breaks the forEach.
			} else {
				notFoundItems++;
			}
		});
		if (notFoundItems === totalItemCount) {
			this.soundService.error();
		}
		this.closeBoxDisabled = this.isCloseBoxDisabled();
		this.closeBoxMessage = null;
	};

	@action.bound
	removeItemFromBox = productId => {
		_.forEach(this.selectedFulfillmentOrder.fulfillmentOrderItems, item => {
			if (item.product.id === productId) {
				item.scannedQuantity--;
				item.isScanned = false;
			}
		});
		this.closeBoxDisabled = this.isCloseBoxDisabled();
	};

	@action.bound
	selectFulfillmentOrder = async fulfillmentOrder => {
		this.initialFulfillmentOrder = fulfillmentOrder;
		this.splittedFulfillmentOrderIds = [];
		this.selectedFulfillmentOrder = fulfillmentOrder;
		this.activeShipmentInfo = null;

		// If FO is not in LabelCreated and it doesn't have active label proceed with printing the label or dimension step
		if ((await this.checkLabelCreatedAndChangeToFinalStep(this.selectedFulfillmentOrder)) == false) {
			this.dimensionsStepErrorMessage =
				this.activeShipmentInfo != null ? 'FULFILLMENTORDER.LABELINGMODE.MESSAGE.EXISTINGLABEL' : null;
			this.selectedFulfillmentOrder.fulfillmentOrderItems = _.map(
				this.selectedFulfillmentOrder.fulfillmentOrderItems,
				item => {
					const scannedQuantity = item.product.productUpcs.some(upc => upc.upc === this.scannedUpc) ? 1 : 0;
					return {
						...item,
						scannedQuantity: scannedQuantity,
						isScanned: item.quantity === scannedQuantity ? true : false,
					};
				}
			); // Adds scannedQuantity and isScanned property to each item and marks initally scanned item as already scanned once
			this.boxedFulfillmentOrders = [];
			this.closeBoxDisabled = this.isCloseBoxDisabled();
			await this.changeStep(2);
		}
	};

	// Split Fulfillment Order.
	@action.bound
	closeBox = async () => {
		this.suspend();
		try {
			this.nonScannedItems = _.filter(this.selectedFulfillmentOrder.fulfillmentOrderItems, item => !item.isScanned);
			// If all items are not scanned, proceed to create (split) new Fulfillment Order containing non scanned items
			if (this.nonScannedItems.length > 0) {
				let itemQuantities = [];
				_.forEach(this.nonScannedItems, item => {
					itemQuantities.push({
						id: item.id,
						nonScannedQuantity: item.quantity - item.scannedQuantity,
						productId: item.productId,
					});
				});
				this.newFulfillmentOrder = await this.fulfillmentOrderProcessingStore.splitFulfillmentOrderLabelingMode(
					{
						fulfillmentOrderId: this.selectedFulfillmentOrder.id,
						fulfillmentOrderItemQuantities: itemQuantities,
					},
					sourceOfChange.splitFullfilmentOrderLabelingMode
				);
				// If FO is split and has the label (it was marked as never shipped and label was persisted), void the label first, then continue
				if (this.newFulfillmentOrder && this.selectedFulfillmentOrder.shipmentInfos.length > 0) {
					await this.shipmentInfoStore.voidLabel(
						{ fulfillmentOrderId: this.selectedFulfillmentOrder.id },
						0,
						sourceOfChange.voidActiveLabelLabelingMode
					);
				}
				this.splittedFulfillmentOrderIds.push(this.newFulfillmentOrder.id);
				if (
					_.some(this.newFulfillmentOrder.fulfillmentOrderItems, item => {
						return item.product.productUpcs.some(upc => upc.upc === this.scannedUpc);
					})
				) {
					this.fulfillmentOrders.push(this.newFulfillmentOrder);
				}
				this.newFulfillmentOrderCreated = true;
			}
			await this.changeStep(3);
			if (
				!this.dimensionsStepErrorMessage &&
				this.selectedFulfillmentOrder.seller.sellerOrganizationInfo.printPackingSlip
			) {
				await this.printPackingSlip();
			}
		} catch (error) {
			this.closeBoxMessage = 'FULFILLMENTORDER.LABELINGMODE.SPLITORDERERROR';
		}
		this.resume();
	};

	@action.bound
	onUpcScan = async event => {
		this.suspend();
		this.steps.labelingStep.form.$('upc').value = event.currentTarget.value.trim(); // Remove whitespace from scanned UPC.
		if (event.key === 'Enter' && event.currentTarget.value.length != 0) {
			if (event.target.name === 'upc') {
				// On first UPC scan, fetch all the Fulfillment Orders associated with scanned UPC.
				if (this.activeStep.number !== 2) {
					this.scannedUpc = this.steps.labelingStep.form.$('upc').value;
					await Promise.all([this.productStore.findProduct({ upc: this.scannedUpc }), this.findFulfillmentOrders()]); // Fetch scanned Product and all Picking Fulfillment Orders associated with the Product.
					this.product = this.productStore.data[0]; // Create endpoint to fetch only one product by search criteria (UPC)?
					this.isUpcScanned = true;
					this.scanUpcMessage = null;
					// If no Fulfillment Order is found.
					if (this.fulfillmentOrders && this.fulfillmentOrders.length === 0) {
						this.isUpcScanned = false;
						if (!this.useRegularPalletizingMode) {
							this.scanUpcMessage = 'FULFILLMENTORDER.LABELINGMODE.MESSAGE.NOTFOUNDORONHOLDWITHPALLET';
						} else {
							this.scanUpcMessage = 'FULFILLMENTORDER.LABELINGMODE.MESSAGE.NOTFOUNDORONHOLD';
						}
						this.soundService.error();
					} else {
						await this.handleScannedFulfillmentOrders();
						this.soundService.success();
					}
				}
				// On second UPC scan, mark item as scanned for selected Fulfillment Order.
				else if (this.activeStep.number === 2 && this.selectedFulfillmentOrder && this.isUpcScanned) {
					this.onItemScan(event.target.value);
				}
			}
		}
		this.resume();
	};

	@action.bound
	checkAndSetActiveShipment = fulfillmentOrder => {
		if (fulfillmentOrder.shipmentInfos !== null && fulfillmentOrder.shipmentInfos.length > 0)
			for (let i = 0; i < fulfillmentOrder.shipmentInfos.length; i++) {
				if (fulfillmentOrder.shipmentInfos[i].status == shipmentInfoStatusEnum.active) {
					this.activeShipmentInfo = fulfillmentOrder.shipmentInfos[i];
					return true;
				}
			}

		return false;
	};

	@action.bound
	checkLabelCreatedAndChangeToFinalStep = async fulfillmentOrder => {
		// If FO is in LabelCreated status and it has active label
		if (
			this.checkAndSetActiveShipment(fulfillmentOrder) &&
			fulfillmentOrder.fulfillmentOrderStatus.abrv === fulfillmentOrderStatus.labelCreated
		) {
			this.steps.finalStep.form.$('trackingNumber').value = this.activeShipmentInfo.trackingNumber;
			await this.changeStep(4);
			return true;
		}
		return false;
	};

	@action.bound
	handleScannedFulfillmentOrders = async () => {
		this.suspend();
		// Sorting is done on the backend. First in line are single item Fulfillment orders.
		if (
			this.fulfillmentOrders[0].fulfillmentOrderItems.length === 1 &&
			this.fulfillmentOrders[0].fulfillmentOrderItems[0].quantity === 1
		) {
			this.selectiongFoStepDisabled = true;
			this.selectedFulfillmentOrder = this.fulfillmentOrders[0];
			this.initialFulfillmentOrder = this.selectedFulfillmentOrder;

			// If FO is not in LabelCreated and it doesn't have active label proceed with printing the label
			if ((await this.checkLabelCreatedAndChangeToFinalStep(this.selectedFulfillmentOrder)) == false) {
				await this.printLabel();
			}
		}
		// If all Fulfillment Orders in the list are multi items, then proceed to step 1 (Selecting Fo step).
		else if (
			(_.every(this.fulfillmentOrders, fulfillmentOrder => {
				return fulfillmentOrder.fulfillmentOrderItems.length > 1;
			}) ||
				_.some(this.fulfillmentOrders, fulfillmentOrder => {
					return _.some(fulfillmentOrder.fulfillmentOrderItems, item => {
						return item.quantity > 1;
					});
				})) &&
			this.fulfillmentOrders.length > 1
		) {
			this.selectiongFoStepDisabled = false;
			await this.changeStep(1);
		}
		// Else, pick the first from the list. This is usually when only one Fulfillment Order is in the list.
		else {
			this.selectiongFoStepDisabled = true;
			this.selectFulfillmentOrder(this.fulfillmentOrders[0]);
		}
		this.resume();
	};

	@action.bound
	onTrackingNumberScan = event => {
		const regex = /^[0-9\b]+$/;
		this.createLabelMessage = null;
		if (this.clearInputOnChange && !event.ctrlKey && regex.test(event.key)) {
			this.clearInputOnChange = false;
			this.steps.finalStep.form.$('trackingNumber').value = event.currentTarget.value.slice(-1);
		} else {
			const valueWithoutWhitespace = event.currentTarget.value.replace(/ /g, '');
			this.steps.finalStep.form.$('trackingNumber').value = valueWithoutWhitespace;
		}

		if (event.key === 'Enter' && event.currentTarget.value.length != 0) {
			if (this.activeStep.number === 4 && this.steps.finalStep.form.$('trackingNumber').value) {
				this.onScanTrackingNumber();
			}
		}
	};

	@action.bound
	findFulfillmentOrders = async () => {
		try {
			const params = {
				carrierTypeId: !this.useRegularPalletizingMode
					? this.palletDropdownStore.value.extra.carrierTypeServiceType.carrierTypeId
					: null,
				carrierServiceTypeId: !this.useRegularPalletizingMode
					? this.palletDropdownStore.value.extra.carrierTypeServiceType.carrierServiceTypeId
					: null,
			};
			this.fulfillmentOrders = (await this.fulfillmentOrderStore.getFulfillmentOrdersForLabeling({
				page: 1,
				rpp: 10, // Set records per page based on modal page size. This is future feature. Max no. of Fulfillment orders on one UPC scan is 10.
				upc: this.steps.labelingStep.form.$('upc').value,
				embed: [
					'shipmentInfos',
					'fulfillmentOrderStatus',
					'fulfillmentOrderItems',
					'fulfillmentOrderItems.product',
					'fulfillmentOrderItems.product.dimensionUnit',
					'fulfillmentOrderItems.product.weightUnit',
					'fulfillmentOrderItems.product.productUpcs',
					'fulfillmentOrderItems.orderLine',
					'order',
					'order.channel',
					'order.channel.channelType',
					'carrierService',
					'carrierService.carrierServiceType',
					'seller',
					'seller.sellerOrganizationInfo',
				],
				...params,
			})).item;
		} catch (error) {
			notificationError({ store: this.rootStore.notificationStore, error });
		}
	};

	@action.bound
	onScanTrackingNumber = async () => {
		try {
			this.isUndoClicked = false;
			const { trackingNumber } = this.steps.finalStep.form.values();
			let params = {};
			if (this.useRegularPalletizingMode) {
				params = {
					palletId: null,
					isRegularPalletizing: true,
					sourceOfChange: sourceOfChange.scanLabel,
				};
			} else {
				params = {
					palletId: this.palletDropdownStore.value.value,
					isRegularPalletizing: false,
					sourceOfChange: sourceOfChange.palletizingModeUpdateShipmentInfoByTrackingNumber,
				};
			}
			const response = await this.shipmentInfoStore.scanTrackingNumber({
				fulfillmentOrderId: this.selectedFulfillmentOrder ? this.selectedFulfillmentOrder.id : null,
				trackingNumber:
					trackingNumber.length === 34 ? trackingNumber.substring(trackingNumber.length - 12) : trackingNumber,
				...params,
			});
			if (response.errorMessage == null) {
				await this.soundService.success();
				this.selectedFulfillmentOrder = response.entity.fulfillmentOrder;
				this.scanLabelMessage = null;
				this.lastAction = 'onTrackingNumberScan';
				this.actionsDone.push(this.lastAction);
				// If selected Fulfillment Order is splitted, add it to boxedFulfillmentOrders list, and select newly created Fulfillment Order as selected for further processing.
				if (this.nonScannedItems.length > 0) {
					_.remove(this.fulfillmentOrders, fulfillmentOrder => {
						return fulfillmentOrder.id === this.selectedFulfillmentOrder.id;
					});
					this.boxedFulfillmentOrders.push(this.selectedFulfillmentOrder);
					this.packagedFulfillmentOrders.push(this.selectedFulfillmentOrder);
					this.timeoutAction = 'stepTwo';
				}
				// Else if there is more multi items left for processing, add the latest one to packagedFulfillmentOrders list and return to step 1 where user will pick another multi item.
				else if (!this.selectiongFoStepDisabled) {
					_.remove(this.fulfillmentOrders, fulfillmentOrder => {
						return fulfillmentOrder.id === this.selectedFulfillmentOrder.id;
					});
					this.boxedFulfillmentOrders = [];
					this.packagedFulfillmentOrders.push(this.selectedFulfillmentOrder);
					this.steps.labelingStep.form.$('upc').value = this.scannedUpc;
					this.timeoutAction = 'stepOne';
				}
				// Else return to UPC scan.
				else {
					this.timeoutAction = 'stepZero';
				}
				this.actionAfterTimeout();
				this.scanLabelMessage = null;
				this.createLabelMessage = null;
				if (!this.useRegularPalletizingMode) {
					await this.palletDropdownStore.reload();
				}
			} else {
				this.scanLabelMessage = response.errorMessage;
				await this.soundService.error();
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
	};

	@action.bound
	printLabel = async () => {
		this.isUndoClicked = false;
		this.newFulfillmentOrderCreated = false;
		const { boxId } = this.steps.dimensionsStep.form.values();
		try {
			this.suspend();
			await this.shipmentInfoStore.createOrGetShipment(
				{
					fulfillmentOrderId: this.selectedFulfillmentOrder.id,
					boxId: boxId,
				},
				sourceOfChange.labelingModeCreateOrGetShipment
			);
			this.lastAction = 'printLabel';
			this.actionsDone.push(this.lastAction);
			this.modalParams.onClose = this.modalParams.data.printLabelCallback;
			if (this.shipmentInfoStore.response.shipmentInfo != null) {
				this.steps.finalStep.form.$(
					'trackingNumber'
				).value = this.shipmentInfoStore.response.shipmentInfo.trackingNumber.toString();
				this.selectedFulfillmentOrder = this.shipmentInfoStore.response.fulfillmentOrder;
				if (this.shipmentInfoStore.response.printNodeResponse === null) {
					this.rootStore.notificationStore.warning('FULFILLMENTORDER.LABELINGMODE.MESSAGE.LABEL.CREATEDANDNOTFOUND');
					this.fileURL = createFileUrl(this.shipmentInfoStore.response.shipmentInfo.label);
					setTimeout(() => {
						printJS(this.fileURL);
					}, 250);
				} else {
					if (this.shipmentInfoStore.response.printNodeResponse.isSuccess) {
						this.rootStore.notificationStore.success('FULFILLMENTORDER.LABELINGMODE.MESSAGE.LABEL.CREATED');
					} else {
						this.rootStore.notificationStore.error(
							'FULFILLMENTORDER.LABELINGMODE.MESSAGE.LABEL.CREATEDANDUNAUTHORIZED'
						);
					}
				}
				await this.changeStep(4);
				if (this.selectedFulfillmentOrder.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.exitScan) {
					this.timeoutAction = 'stepZero';
					this.actionAfterTimeout();
				}
			} else {
				await this.changeStep(4);
				if (this.shipmentInfoStore.response.shipmentData != null) {
					if (
						this.shipmentInfoStore.response.shipmentData.failureNotification &&
						this.shipmentInfoStore.response.shipmentData.failureNotification.length > 0
					) {
						this.createLabelMessage = this.shipmentInfoStore.response.shipmentData.failureNotification[0];
					} else if (
						this.shipmentInfoStore.response.shipmentData.notifications &&
						this.shipmentInfoStore.response.shipmentData.notifications.length > 0
					) {
						this.createLabelMessage = this.shipmentInfoStore.response.shipmentData.notifications[0];
					}
				} else if (this.shipmentInfoStore.response.message != null) {
					if (this.shipmentInfoStore.response.errors) {
						const errors = _.map(this.shipmentInfoStore.response.errors, error => {
							return translate(error);
						});
						this.createLabelMessage = `${translate(this.shipmentInfoStore.response.message)} ${
							errors ? errors.join(' ') : null
						}`;
					} else {
						this.createLabelMessage = this.shipmentInfoStore.response.message;
					}
				}
				await this.soundService.error();
			}
		} catch (err) {
			if (
				err.data != null &&
				err.data.error != null &&
				err.data.error.InnerExceptions != null &&
				err.data.error.InnerExceptions.length > 0 &&
				err.data.error.InnerExceptions[0].Message
			) {
				this.createLabelMessage = err.data.error.InnerExceptions[0].Message;
			} else {
				this.createLabelMessage = 'FULFILLMENTORDER.LABELINGMODE.MESSAGE.NOTFOUNDORONHOLD';
			}
			await this.soundService.error();
		}
		this.resume();
	};

	@action.bound
	printPackingSlip = async () => {
		this.suspend();
		try {
			const pdfResponse = await this.packingSlipStore.generatePdf(this.selectedFulfillmentOrder.id);
			if (pdfResponse.printedWithPrintNode) {
				this.rootStore.notificationStore.success('FULFILLMENTORDER.LABELINGMODE.MESSAGE.PACKINGSLIP.CREATED');
			} else {
				const fileURL = createFileUrl(pdfResponse.packingSlip);
				printJS(fileURL);
				this.rootStore.notificationStore.warning(
					'FULFILLMENTORDER.LABELINGMODE.MESSAGE.PACKINGSLIP.CREATEDANDNOTFOUND'
				);
			}
		} catch (err) {
			this.rootStore.notificationStore.error(['FULFILLMENTORDER.LABELINGMODE.PACKINGSLIP.ERROR']);
		}
		this.resume();
	};

	getRemoveItemConfirmModalProps = productId => {
		return {
			onConfirm: async () => this.removeItemFromBox(this.rootStore.viewStore.selectedConfirmModal.data),
			message: ['FULFILLMENTORDER.LABELINGMODE.MESSAGE.REMOVEITEMCONFIRMATION'],
			data: productId,
		};
	};

	@action.bound
	onBoxChange = value => {
		if (_.get(value, 'label') == 'Add new template') {
			this.showCreateTemplate = true;
			this.steps.dimensionsStep.form.$('boxId').resetValidation();
			this.steps.dimensionsStep.form.$('boxId').set('rules', '');
		} else {
			this.showCreateTemplate = false;
			this.steps.dimensionsStep.form.$('boxId').set('rules', 'required');
		}
		this.resetMessages();
	};

	@action.bound
	onCreateTemplateCancel = () => {
		this.steps.dimensionsStep.form.clear();
		this.steps.dimensionsStep.form.$('boxId').invalidate();
		this.boxDropdownStore.clear();
		this.showCreateTemplate = false;
		this.steps.dimensionsStep.form.$('boxId').set('rules', 'required');
	};

	@action.bound
	onCreateTemplateSubmit = async () => {
		try {
			const { name, width, height, length } = this.steps.dimensionsStep.form.values();
			const boxValues = {
				name,
				width,
				height,
				length,
				organizationId: this.isSuperAdminOrganization ? this.selectedFulfillmentOrder.sellerId : null,
			};
			const response = await this.lookupStore.createLookup(boxValues, lookupRouteName.box, sourceOfChange.createLookup);
			if (response.message) {
				this.steps.dimensionsStep.form.$('name').invalidate(response.message);
			} else {
				var createdBox = this.lookupStore.item;
				if (createdBox) {
					this.showCreateTemplate = false;
					this.steps.dimensionsStep.form.$('name').clear();
					this.steps.dimensionsStep.form.$('height').clear();
					this.steps.dimensionsStep.form.$('width').clear();
					this.steps.dimensionsStep.form.$('length').clear();
					this.boxDropdownStore.reload();
					this.boxDropdownStore.setValue(createdBox);
					this.steps.dimensionsStep.form.$('boxId').set('rules', 'required');
				}
			}
		} catch (err) {}
	};

	@action.bound
	async onUndo() {
		const lastActionDone = _.last(this.actionsDone);
		this.isUndoClicked = true;

		try {
			// Undo from Label Created status to Picking.
			if (lastActionDone === 'printLabel') {
				const fulfillmentOrder = await this.shipmentInfoStore.undoLabelCreated(
					{ fulfillmentOrderId: this.selectedFulfillmentOrder.id },
					sourceOfChange.undoPrintLabelLabelingMode
				);
				this.selectedFulfillmentOrder.fulfillmentOrderStatus = fulfillmentOrder.fulfillmentOrderStatus;
				this.packagedFulfillmentOrders.pop();
				this.removeElementFromActionsList();
				this.lastAction = null;
				if (
					// If single item, return to UPC scan because step 1, 2 and 3 were skipped
					this.fulfillmentOrders[0].fulfillmentOrderItems.length === 1 &&
					this.fulfillmentOrders[0].fulfillmentOrderItems[0].quantity === 1
				) {
					this.isUpcScanned = false;
					this.resetSteps();
				} else {
					await this.returnToPreviousStep();
				}
			}
			// Undo from Packaged status to Label Created.
			else if (lastActionDone === 'onTrackingNumberScan') {
				const fulfillmentOrder = await this.fulfillmentOrderStore.undoPackaged(
					{ fulfillmentOrderId: this.selectedFulfillmentOrder.id },
					sourceOfChange.undoScanLabelLabelingMode
				);
				this.selectedFulfillmentOrder.fulfillmentOrderStatus = fulfillmentOrder.fulfillmentOrderStatus;
				this.boxedFulfillmentOrders.pop();
				this.packagedFulfillmentOrders.pop();
				this.removeElementFromActionsList();
				this.lastAction = null;
			}
			// Undo from Palletized status to Label Created.
			else if (this.lastAction === 'palletize') {
				await this.shipmentInfoStore.undoPalletize(
					{
						trackingNumber: this.steps.finalStep.form.$('trackingNumber').value,
						isFromLabelingMode: true,
					},
					sourceOfChange.undoPalletizeLabelingMode
				);

				//this.selectedFulfillmentOrder.fulfillmentOrderStatus = fulfillmentOrder.fulfillmentOrderStatus;
				this.rootStore.notificationStore.success(
					'Undo from Palletize to Label Created successful. UI Status change not yet implemented.'
				);
				this.boxedFulfillmentOrders.pop();
				this.packagedFulfillmentOrders.pop();
				this.removeElementFromActionsList();
				this.lastAction = null;
			}
			// Nothing to Undo
			else {
				this.rootStore.notificationStore.error('FULFILLMENTORDER.LABELINGMODE.MESSAGE.UNDONOTHING');
			}
		} catch (err) {
			this.rootStore.notificationStore.error('FULFILLMENTORDER.LABELINGMODE.MESSAGE.UNDOERROR');
		}
	}

	@action.bound
	onAbort = async () => {
		this.suspend();
		try {
			await this.fulfillmentOrderProcessingStore.abortLabelingMode(
				{
					initialFulfillmentOrder: this.initialFulfillmentOrder,
					splittedFulfillmentOrderIds: this.splittedFulfillmentOrderIds,
				},
				sourceOfChange.abortLabelingMode
			);
			this.rootStore.notificationStore.success([
				'FULFILLMENTORDER.LABELINGMODE.MESSAGE.ABORTSUCCESS',
				{ id: this.initialFulfillmentOrder.fulfillmentOrderId },
			]);
		} catch (err) {
			this.rootStore.notificationStore.error([
				'FULFILLMENTORDER.LABELINGMODE.MESSAGE.ABORTFAILURE',
				{ id: this.initialFulfillmentOrder.fulfillmentOrderId },
			]);
		}
		this.resume();
	};

	@action.bound
	removeElementFromActionsList = () => {
		const actionIndex = _.findIndex(this.actionsDone, item => item === this.lastAction);
		this.actionsDone.splice(actionIndex, 1);
	};

	getAbortConfirmModalProps = () => {
		return {
			onConfirm: async () => {
				this.isUndoClicked = true;
				this.onAbort();
				this.modalParams.success();
			},
			onClose: async () => {
				this.isUndoClicked = false;
				if (this.timeoutAction != null) {
					this.actionAfterTimeout();
				}
			},
			message: ['FULFILLMENTORDER.LABELINGMODE.MESSAGE.ABORTCONFIRMATION'],
		};
	};

	@action.bound
	actionAfterTimeout() {
		setTimeout(() => {
			// If user didn't click undo or Abort, continue with resetSteps.
			if (!this.isUndoClicked) {
				if (this.timeoutAction === 'stepTwo') {
					this.selectedFulfillmentOrder = this.newFulfillmentOrder;
					_.forEach(this.selectedFulfillmentOrder.fulfillmentOrderItems, item => {
						item.scannedQuantity = 0;
						item.isScanned = false;
					});
					this.closeBoxDisabled = this.isCloseBoxDisabled();
					this.steps.dimensionsStep.isCompleted = false;
					this.changeStep(2);
				} else if (this.timeoutAction === 'stepOne') {
					this.selectedFulfillmentOrder = null;
					this.resetSteps();
					this.changeStep(1);
				} else if (this.timeoutAction === 'stepZero') {
					this.resetSteps();
					this.isUpcScanned = false;
					this.steps.labelingStep.form.clear();
				}
				this.timeoutAction = null;
			}
		}, 4000);
	}

	@action.bound
	openAbortConfirmModal() {
		this.isUndoClicked = true;
		this.openConfirmationModal(this.getAbortConfirmModalProps());
	}

	@action.bound
	setInputs = (input, field) => {
		const key = field.key + 'Input';
		this[key] = input;
	};

	@action.bound
	resetSteps = () => {
		_.forEach(this.steps, step => {
			step.isCompleted = false;
		});
	};

	@action.bound
	handleShowAll = async (fulfillmentOrderId, packagedOrders = false) => {
		_.forEach(packagedOrders ? this.packagedFulfillmentOrders : this.fulfillmentOrders, fulfillmentOrder => {
			if (fulfillmentOrder.id === fulfillmentOrderId) {
				fulfillmentOrder.showAll = true;
			}
		});
		await this.changeStep(1); // This will force re-render, but this function should not be called. Unable to find better solution at the moment.
	};

	@action.bound
	resetMessages = () => {
		this.scanUpcMessage = null;
		this.scanLabelMessage = null;
		this.closeBoxMessage = null;
		this.createLabelMessage = null;
		this.dimensionsStepErrorMessage = null;
	};

	@action.bound
	setRef = ref => {
		if (ref != null) {
			if (ref.constructor.name === 'Async') {
				this.selectReference = ref;
			} else {
				this.barcodeRef = ref;
			}
		}
	};

	isCloseBoxDisabled = () => {
		if (
			_.every(this.selectedFulfillmentOrder.fulfillmentOrderItems, item => {
				return item.scannedQuantity === 0;
			})
		) {
			// Close box button is enabled only if at least one of the items is scanned.
			return true;
		} else {
			return false;
		}
	};

	getPalletDeleteConfirmModalProps = item => {
		return {
			onConfirm: async () => await this.deletePallet(),
			message: ['LOOKUPDELETECONFIRM.PALLET', { name: item.label }],
			data: item,
		};
	};
}

export default labelingModeViewStore;



// WEBPACK FOOTER //
// ./src/modules/order/stores/LabelingModeViewStore.js